import { useInView, useTimedStage } from "@/components/living/useScrollStage";

const SYSTEMS = ["WhatsApp", "Excel", "CRM", "Tally", "Email", "ERP", "Phone"];

const LOOP = [
  { who: "AI", does: "identifies an overdue customer", detail: "Invoice #1187 · 34 days · ₹1,84,000" },
  { who: "AI", does: "drafts the follow-up", detail: "Polite, in your tone, with the invoice attached" },
  { who: "Manager", does: "approves", detail: "One tap. Or edits it first." },
  { who: "System", does: "sends the WhatsApp message", detail: "Delivered 10:04 · read 10:11" },
  { who: "System", does: "updates the CRM", detail: "Promise to pay: Friday · reminder set" },
];

const WRAP = "mx-auto max-w-[1400px] px-5 md:px-8";

/** Two trust arguments: we connect what you have, and a person stays in control. */
export default function TrustScene() {
  return (
    <section className="border-b border-ink" id="trust" data-testid="section-trust">
      <div className={`${WRAP} section-pad grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10`}>
        <div className="lg:col-span-6">
          <p className="eyebrow">Works with what you have</p>
          <h2 className="display mt-5 text-[34px] md:text-[44px] leading-[0.98]">
            We do not need to replace your business stack.
          </h2>
          <p className="mt-4 max-w-[42ch] text-lg text-ink/75">
            WhatsApp, Excel, your CRM, Tally, email, the ERP, the phone line. They stay.
            AUT labs sits between them and turns what they hold into action.
          </p>
          <StackDiagram />
        </div>

        <div className="lg:col-span-6">
          <p className="eyebrow">A person stays in control</p>
          <h2 className="display mt-5 text-[34px] md:text-[44px] leading-[0.98]">
            AI prepares. A human reviews. The system executes.
          </h2>
          <p className="mt-4 max-w-[42ch] text-lg text-ink/75">
            Anything that touches money, customers or commitments waits for a yes.
            Everything is tracked afterwards.
          </p>
          <HumanLoop />
        </div>
      </div>
    </section>
  );
}

function StackDiagram() {
  const { ref, on } = useInView(0.4);
  const n = SYSTEMS.length;
  const top = 18, gap = 30;
  const hubY = top + ((n - 1) * gap) / 2;
  return (
    <svg ref={ref} className="viz mt-8" style={{ maxWidth: 560 }} viewBox="0 0 420 230" data-on={on} role="img" aria-label="Seven existing systems connect to AUT labs, which produces actions">
      {SYSTEMS.map((s, i) => {
        const y = top + i * gap;
        return (
          <g key={s}>
            <rect x="0" y={y - 11} width="92" height="22" fill="#F7F4EF" stroke="#16130F" />
            <text x="46" y={y + 4} textAnchor="middle" fontSize="11" fontFamily="Inter Tight" fill="#16130F">{s}</text>
            <path className={`draw d${Math.min(5, (i % 5) + 1)}`} pathLength="1" d={`M92 ${y} H150 Q170 ${y} 170 ${y < hubY ? y + 14 : y > hubY ? y - 14 : y} L190 ${hubY}`} stroke="#2B3FF0" strokeWidth="1.6" fill="none" />
          </g>
        );
      })}
      <rect className="fade d3" x="190" y={hubY - 22} width="96" height="44" fill="#2B3FF0" />
      <text className="fade d3" x="238" y={hubY + 6} textAnchor="middle" fontSize="18" fontFamily="Instrument Serif" fill="#F7F4EF">AUT labs</text>
      <path className="draw d4" pathLength="1" d={`M286 ${hubY} H330`} stroke="#2B3FF0" strokeWidth="2" fill="none" />
      <path className="fade d5" d={`M326 ${hubY - 5} l6 5 l-6 5`} stroke="#2B3FF0" strokeWidth="2" fill="none" />
      <rect className="fade d5" x="334" y={hubY - 16} width="86" height="32" fill="#F7F4EF" stroke="#16130F" />
      <text className="fade d5" x="377" y={hubY + 4} textAnchor="middle" fontSize="11" fontFamily="Inter Tight" fill="var(--ember-deep)" letterSpacing="1.5">ACTION</text>
    </svg>
  );
}

function HumanLoop() {
  const { ref, stage } = useTimedStage(4);
  return (
    <div ref={ref} className="mt-8 ui max-w-[560px]" aria-label="Example of an approved action">
      <div className="ui__bar"><span>Collections · today</span><span>Awaiting approval · 1</span></div>
      <ol>
        {LOOP.map((s, i) => (
          <li key={s.does} className="ui__row" data-on={stage >= i} data-live={stage === i}>
            <span>
              <span className={`eyebrow text-[10px] ${s.who === "Manager" ? "" : "text-ink/75"}`} style={s.who === "Manager" ? undefined : { color: "rgba(22,19,15,.75)" }}>{s.who}</span>
              <span className="block text-[14px] font-medium mt-0.5">{s.does}</span>
              <span className="block text-[12px] text-ink/60">{s.detail}</span>
            </span>
            {s.who === "Manager" ? (
              <span className={`pill text-[11px] min-h-[30px] px-3 py-1 ${stage >= i ? "pill-cobalt" : ""}`} aria-hidden="true" style={{ cursor: "default" }}>
                {stage > i ? "Approved" : "Approve"}
              </span>
            ) : (
              <span className={`ui__val ${stage > i ? "ui__val--cobalt" : ""}`}>{stage > i ? "Done" : stage === i ? "Now" : "Waiting"}</span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
